import React, { useMemo, useState } from 'react';
import styled from 'styled-components';

import { colors, radius, spacing } from '../../tokens';
import { matchCountries } from './countryMatch';
import { FieldLabel, FieldNote } from './styles';

export type Country = {
  name: string;
  flag: string;
};

const Wrapper = styled.div`
  position: relative;
`;

const Label = styled(FieldLabel)`
  margin-bottom: 4px;
`;

const Input = styled.input<{ hasError: boolean }>`
  width: 100%;
  height: 48px;
  padding: 0 ${spacing.md};
  border: 1px solid ${({ hasError }) => (hasError ? '#f74656' : colors.border)};
  border-radius: 2px;
  background-color: #f4f7f9;
  font-size: 14px;
  color: ${colors.text};
  outline: none;

  &:focus {
    border-color: ${colors.brand};
  }
`;

const Dropdown = styled.ul`
  position: absolute;
  z-index: 5;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  max-height: 240px;
  overflow-y: auto;
  margin: 0;
  padding: ${spacing.xs} 0;
  list-style: none;
  background-color: ${colors.surface};
  border: 1px solid ${colors.border};
  border-radius: ${radius.medium};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
`;

const NoResultItem = styled.li`
  padding: ${spacing.sm} ${spacing.md};
  font-size: 14px;
  color: ${colors.textMuted};
`;

const DropdownItem = styled.li`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  padding: ${spacing.sm} ${spacing.md};
  font-size: 14px;
  cursor: pointer;

  &:hover {
    background-color: ${colors.brandSoft};
  }
`;

const ErrorNote = styled(FieldNote)`
  color: #f74656;
`;

const DEFAULT_NOTE = 'Commencez à taper pour filtrer la liste, puis sélectionnez votre pays.';
const LOADING_NOTE = 'Chargement de la liste des pays…';

type CountryFieldProps = {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  countries: Country[];
  isLoading?: boolean;
  errorMessage?: string;
};

/**
 * Type-ahead combobox over a country list fetched by the caller (see `useCountries`) — kept
 * presentation-only so both `nationalite` and `pays_naissance` can share one fetch.
 */
const CountryField: React.FC<CountryFieldProps> = ({
  id, label, value, onChange, countries, isLoading = false, errorMessage,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const matches = useMemo(() => matchCountries(value, countries), [value, countries]);

  const selectCountry = (country: Country): void => {
    onChange(country.name);
    setIsOpen(false);
  };

  let note = DEFAULT_NOTE;
  if (isLoading) {
    note = LOADING_NOTE;
  }

  return (
    <Wrapper>
      <Label as="label" htmlFor={id}>{label}</Label>
      <Input
        id={id}
        name={id}
        type="text"
        autoComplete="off"
        placeholder="Tapez les premières lettres…"
        value={value}
        hasError={Boolean(errorMessage)}
        disabled={isLoading}
        onChange={(event) => {
          onChange(event.target.value);
          setIsOpen(true);
        }}
        onFocus={() => setIsOpen(true)}
        onBlur={() => setTimeout(() => setIsOpen(false), 120)}
      />
      {isOpen && matches.length === 0 && value.trim() !== '' && (
        <Dropdown>
          <NoResultItem>Aucun pays ne correspond à votre recherche.</NoResultItem>
        </Dropdown>
      )}
      {isOpen && matches.length > 0 && (
        <Dropdown>
          {matches.map((country) => (
            <DropdownItem
              key={country.name}
              onMouseDown={(event) => {
                event.preventDefault();
                selectCountry(country);
              }}
            >
              <span>{country.flag}</span>
              <span>{country.name}</span>
            </DropdownItem>
          ))}
        </Dropdown>
      )}
      {errorMessage ? <ErrorNote>{errorMessage}</ErrorNote> : <FieldNote>{note}</FieldNote>}
    </Wrapper>
  );
};

export { CountryField };
